import React, { useState } from "react";
import { ChevronDown, ChevronUp, Plus, X, ClipboardList } from "lucide-react";

const SERVICE_LABEL = {
  venue: "Venue",
  photography: "Photography",
  caterers: "Catering",
  decors: "Decoration",
};

/**
 * Guided "what do you offer" questionnaire shown before generating packages.
 * Its answers are passed to the AI so the generated packages match what the
 * vendor actually provides (tier count, budget band, must-haves, audience).
 *
 * Controlled: parent owns `value` (the questionnaire object) and `onChange`.
 */
export default function PackageQuestionnaire({ serviceType, value, onChange }) {
  const [open, setOpen] = useState(true);
  const [chipDraft, setChipDraft] = useState("");

  const q = value || {};
  const set = (patch) => onChange({ ...q, ...patch });

  const addChip = () => {
    const v = chipDraft.trim();
    if (!v) return;
    const next = [...(q.mustHave || [])];
    if (!next.includes(v)) next.push(v);
    set({ mustHave: next });
    setChipDraft("");
  };

  const removeChip = (chip) =>
    set({ mustHave: (q.mustHave || []).filter((c) => c !== chip) });

  const tierCount = q.tierCount || 3;

  return (
    <div className="border border-gray-200 rounded-xl mb-4 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <ClipboardList size={16} className="text-purple-500" />
          Tell us what you offer
          <span className="text-xs font-normal text-gray-400">(guides the AI)</span>
        </span>
        {open ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>

      {open && (
        <div className="p-4 space-y-4">
          {/* Service type (read-only) */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Service</label>
            <div className="mt-1">
              <span className="inline-block px-3 py-1 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-xs font-medium">
                {SERVICE_LABEL[serviceType] || serviceType || "—"}
              </span>
            </div>
          </div>

          {/* Number of tiers */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Number of packages</label>
            <div className="mt-1.5 inline-flex rounded-lg border border-gray-200 overflow-hidden">
              {[2, 3, 4].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => set({ tierCount: n })}
                  className={`px-4 py-1.5 text-sm font-medium transition ${
                    tierCount === n ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {n} tiers
                </button>
              ))}
            </div>
          </div>

          {/* Budget range */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Price band (₹) <span className="font-normal normal-case text-gray-400">— optional</span>
            </label>
            <div className="mt-1.5 flex items-center gap-2">
              <input
                type="number"
                min="0"
                placeholder="Min"
                value={q.budgetMin ?? ""}
                onChange={(e) => set({ budgetMin: e.target.value })}
                className="w-32 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 hover:border-gray-300 outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400"
              />
              <span className="text-gray-400 text-sm">to</span>
              <input
                type="number"
                min="0"
                placeholder="Max"
                value={q.budgetMax ?? ""}
                onChange={(e) => set({ budgetMax: e.target.value })}
                className="w-32 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 hover:border-gray-300 outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400"
              />
            </div>
          </div>

          {/* Must-have inclusions */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Must-have inclusions <span className="font-normal normal-case text-gray-400">— optional</span>
            </label>
            <div className="mt-1.5 flex gap-2">
              <input
                type="text"
                value={chipDraft}
                onChange={(e) => setChipDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addChip();
                  }
                }}
                placeholder="e.g. Drone shots, In-house parking…"
                className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 hover:border-gray-300 outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400"
              />
              <button
                type="button"
                onClick={addChip}
                className="px-3 py-2 rounded-lg bg-purple-100 text-purple-700 hover:bg-purple-200 transition"
              >
                <Plus size={16} />
              </button>
            </div>
            {(q.mustHave || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {q.mustHave.map((chip) => (
                  <span
                    key={chip}
                    className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs"
                  >
                    {chip}
                    <button type="button" onClick={() => removeChip(chip)} className="hover:text-rose-500">
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Style / audience */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Target audience / style <span className="font-normal normal-case text-gray-400">— optional</span>
            </label>
            <input
              type="text"
              value={q.audience ?? ""}
              onChange={(e) => set({ audience: e.target.value })}
              placeholder="e.g. Luxury weddings, budget corporate events"
              className="mt-1.5 w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 hover:border-gray-300 outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400"
            />
          </div>
        </div>
      )}
    </div>
  );
}
